<script>
  /** @extends {"./AccordionSkeleton.svelte"} AccordionSkeletonProps */

  /**
   * Specify the title of the accordion item heading.
   * Alternatively, use the "heading" slot (e.g., `<div slot="heading">...</div>`)
   */
  export let heading = "heading";

  /** Set to `true` to open the first accordion item */
  export let open = false;

  /**
   * Specify alignment of accordion item chevron icon
   * @type {"start" | "end"}
   */
  export let align = "end";

  /**
   * Specify the size of the accordion
   * @type {boolean }
   */
  export let small = false;

  /** Set to `true` to disable the accordion */
  export let disabled = false;

  /** Set to `true` to display the skeleton state */
  export let skeleton = false;

  /** Set to `true` to use a non-bold heading */
  export let boldHeading = true;

  /** Set to `true` to use paxbot style */
  export let paxbot = false;

  /** Set to `true` to use the seamless style without radius and outer padding */
  export let seamless = false;

  /** Set to `true` to restore horizontal padding within a seamless accordion */
  export let seamlessPadding = false;

  /** Specify the accordion width. */
  export let width = "100%";

  /** Specify the ARIA label for the accordion item chevron icon */
  export let iconDescription = "Expand/Collapse";

  import { setContext } from "svelte";
  import { writable } from "svelte/store";
  import AccordionSkeleton from "./AccordionSkeleton.svelte";
  import AccordionChevron from "../icons/AccordionChevron.svelte";

  const disableItems = writable(disabled);

  $: disableItems.set(disabled);

  let animation = undefined;

  setContext("Accordion", { disableItems });
</script>

<!-- svelte-ignore a11y-mouse-events-have-key-events -->
{#if skeleton}
  <AccordionSkeleton
    {...$$restProps}
    align="{align}"
    small="{small}"
    paxbot="{paxbot}"
    width="{width}"
    open="{open}"
    on:click
    on:mouseover
    on:mouseenter
    on:mouseleave
  />
{:else}
  <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
  <div
    class:bx--accordion="{true}"
    class:bx--accordion--start="{align === 'start'}"
    class:bx--accordion--end="{align === 'end'}"
    class:bx--accordion--sm="{small}"
    class:bx--accordion--paxbot="{paxbot === true}"
    class:bx--accordion--seamless="{seamless === true}"
    class:bx--accordion--seamless-padding="{seamless === true &&
      seamlessPadding === true}"
    style:width="{width}"
    class:bx--accordion__item="{true}"
    class:bx--accordion__item--active="{open}"
    class:bx--accordion__item--disabled="{disabled}"
    class:bx--accordion__item--expanding="{animation === 'expanding'}"
    class:bx--accordion__item--collapsing="{animation === 'collapsing'}"
    {...$$restProps}
    on:animationend
    on:animationend="{() => {
      animation = undefined;
    }}"
  >
    <button
      type="button"
      class:bx--accordion__heading="{true}"
      heading="{iconDescription}"
      aria-expanded="{open}"
      disabled="{disabled}"
      on:click
      on:click="{() => {
        open = !open;
        animation = open ? 'expanding' : 'collapsing';
      }}"
      on:mouseover
      on:mouseenter
      on:mouseleave
      on:keydown
      on:keypress="{() => {}}"
      on:keydown="{({ key }) => {
        if (open && key === 'Escape') {
          open = false;
        }
      }}"
    >
      <AccordionChevron
        class="bx--accordion__arrow"
        aria-label="{iconDescription}"
      />
      <div class:bx--accordion__title="{true}" class:bold="{boldHeading}">
        <slot name="heading">{heading}</slot>
      </div>
    </button>
    <div class:bx--accordion__content="{true}">
      <slot />
    </div>
  </div>
{/if}
